import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, debounceTime, distinctUntilChanged, filter, from, map, merge, Subject, switchMap, takeUntil, tap } from "rxjs";
import { LogService } from "../baseServices/logging/log.service";
import { TimeRecordConstants } from "../constant/time-record-constants";
import { TimeRecord } from "../entities/timeRecord";
import { DateTimeHelper, DateTimeType } from "../utils/date-time-helper";
import { AppUserTimeEntryMethodService } from "./app-user-time-entry-method.service";
import { AppUserService } from "./app-user.service";
import { TimeoutSchedulerService } from "./timeout-scheduler.service";
import { TimeRecordService } from "./timeRecord.service";

export enum ClockState{
  WORKING = 'Working',
  REST_BREAK = 'Rest Break',
  MEAL_BREAK = 'Meal Break',
  CLOCKED_OUT = 'Clocked Out',
}

export interface UserClockState {
  state: ClockState;
  sinceTime: string | null;
  timeZoneId: string | null;
  activeTimeRecord: TimeRecord | null;
}


/** Owns confirmed clock state; failed current reads publish unknown and retry instead of retaining location-tracking eligibility. */
@Injectable({ providedIn: "root" })
export class AppUserClockStateService implements OnDestroy {
  private static readonly TIMEOUT_ID = 'app-user-next-timerecord-end';
  readonly clockStateSubject = new BehaviorSubject<UserClockState | null>(null);
  
  // Derived boolean observables
  public readonly clockState$ = this.clockStateSubject.asObservable();
  private isState$(state: ClockState) { return this.clockState$.pipe(map(s => s?.state === state), distinctUntilChanged()); }
  public readonly isWorking$ = this.isState$(ClockState.WORKING);
  public readonly isOnRestBreak$ = this.isState$(ClockState.REST_BREAK);
  public readonly isOnMealBreak$ = this.isState$(ClockState.MEAL_BREAK);
  public readonly isClockedOut$ = this.isState$(ClockState.CLOCKED_OUT);

  // Synchronous snapshot helpers
  public get clockState(): UserClockState | null { return this.clockStateSubject.value; }
  public get isWorking(): boolean { return this.clockState?.state === ClockState.WORKING; }
  public get isOnRestBreak(): boolean { return this.clockState?.state === ClockState.REST_BREAK; }
  public get isOnMealBreak(): boolean { return this.clockState?.state === ClockState.MEAL_BREAK; }
  public get isClockedOut(): boolean { return this.clockState?.state === ClockState.CLOCKED_OUT; }

  private readonly destroy$ = new Subject<void>();
  private loadRevision = 0;
  private retryDelayMs = 1000;

  constructor(
    private logService: LogService,
    private appUserService: AppUserService,
    private appUserTimeEntryMethodService: AppUserTimeEntryMethodService,
    private timeRecordService: TimeRecordService,
    private timeoutScheduler: TimeoutSchedulerService
  ) {
    this.loadSubscriptions();
  }

  ngOnDestroy(): void {
    this.loadRevision++;
    this.cancelTimeout();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadSubscriptions(): void {
    // Combined subscription: on user change, time entry method change, relevant time record change, or boundary timeout, reload state
    merge(
      this.appUserService.currentUserId$,
      this.appUserService.currentUser$,
      this.appUserTimeEntryMethodService.isClockTimeEntryUser$,
      this.timeRecordService.changeEmitted$.pipe(filter(e => e.data.EmployeeId === this.appUserService.currentUserId)),
      this.timeoutScheduler.getTimeoutById(AppUserClockStateService.TIMEOUT_ID)
    )
      .pipe(
        tap(() => this.loadRevision++),
        debounceTime(50),
        takeUntil(this.destroy$),
        switchMap(() => from(this.loadUserClockState()))
      )
      .subscribe();
  }

  /**
   * Determines and sets the user's current clock state.
   * The state is derived from the user's current and upcoming time records in relation to the current local time.
   * 
   * Logic:
   * - If user is not logged in → null
   * - If user is not a clock-entry user (e.g. not a time user or block-entry user) → null
   * - If the current time record is a rest break → REST_BREAK
   * - If the current time record is a meal break → MEAL_BREAK
   * - If an upcoming time record is working → WORKING
   * - If an upcoming time record is a rest break → REST_BREAK
   * - If an upcoming time record is a meal break → MEAL_BREAK
   * - If there is no upcoming record and the current record is working and open → WORKING
   * - If there is no upcoming record and the current record is closed → CLOCKED_OUT
   * - If there is no current or upcoming record → CLOCKED_OUT
   * - Otherwise (unexpected case) → null
   * 
   * When rounding up to an upcoming time record's status, schedules a timeout at the record's ClockOut time to automatically refresh state when it ends.
   */
  private async loadUserClockState(): Promise<void> {
    const revision = this.loadRevision;
    const user = this.appUserService.currentUser;
    if (!user) {
      this.setNullClockStatus("User is not logged in or user data is not available.");
      this.cancelTimeout();
      return;
    }

    // Not a clock-entry user (either is not a time user, is a MTBE user, or is not logged in)
    if (!this.appUserTimeEntryMethodService.isClockTimeEntryUser) {
      this.setNullClockStatus("User is not a clock-entry user.");
      this.cancelTimeout();
      return;
    }

    const now = DateTimeHelper.getLocalNow();
    const nowRounded = DateTimeHelper.roundDateTime(now, TimeRecordConstants.ROUND_TO_MINUTES);
    const timeZoneId = DateTimeHelper.getTimeZoneId();
    
    let currentTimeRecord: TimeRecord | null; // The time record bounding the current time
    let upcomingTimeRecord: TimeRecord | null; // The time record bounding the rounded time. This may be the same as currentTimeRecord.
    let openTimeRecord: TimeRecord | null; // The time record that is currently open. This will likely be the same as currentTimeRecord, but could be different if the user deleted their rest break right after starting it leaving an open time record 7-15 minutes from now.
    try {
      [currentTimeRecord, upcomingTimeRecord, openTimeRecord] = await Promise.all([
        this.timeRecordService.getTimeRecordEnclosingDatetime(user.Id, now, timeZoneId),
        this.timeRecordService.getTimeRecordEnclosingDatetime(user.Id, nowRounded, timeZoneId),
        this.timeRecordService.getOpenTimeRecord(user.Id, now)
      ]);
    }
    catch (err) {
      this.logService.error("AppUserClockState.LoadFailed", err);
      if (revision === this.loadRevision && user.Id === this.appUserService.currentUserId) {
        this.suspendAndRetry("Current working state is unavailable.");
      }
      return;
    }
    if (revision !== this.loadRevision || user.Id !== this.appUserService.currentUserId) { return; }
    
    // Current time record is a rest break - user is on a rest break. We don't want to cut their break off early by rounding.
    if (currentTimeRecord && this.timeRecordService.checkIsTimeRecordRestBreak(currentTimeRecord)) {
      this.setUserClockStatus(ClockState.REST_BREAK, currentTimeRecord.ClockIn, timeZoneId, currentTimeRecord);
      this.scheduleTimeout(currentTimeRecord.ClockOut, "RestBreakEnd");
      return;
    }

    // Upcoming time record is working - user started or resumed their work a bit early
    if (upcomingTimeRecord && this.timeRecordService.checkIsTimeRecordWorking(upcomingTimeRecord)) {
      this.setUserClockStatus(ClockState.WORKING, upcomingTimeRecord.ClockIn, timeZoneId, upcomingTimeRecord);
      this.scheduleTimeout(upcomingTimeRecord.ClockOut, "WorkingRecordEnd(EarlyStart)");
      return;
    }

    // Upcoming time record is a rest break - user started their rest break a bit early
    if (upcomingTimeRecord && this.timeRecordService.checkIsTimeRecordRestBreak(upcomingTimeRecord)) {
      this.setUserClockStatus(ClockState.REST_BREAK, upcomingTimeRecord.ClockIn, timeZoneId, upcomingTimeRecord);
      this.scheduleTimeout(upcomingTimeRecord.ClockOut, "UpcomingRestBreakEnd(EarlyStart)");
      return;
    }

    // Upcoming time record is a meal break - user started their meal break a bit early
    if (upcomingTimeRecord && this.timeRecordService.checkIsTimeRecordMealBreak(upcomingTimeRecord)) {
      this.setUserClockStatus(ClockState.MEAL_BREAK, upcomingTimeRecord.ClockIn, timeZoneId, upcomingTimeRecord);
      this.scheduleTimeout(upcomingTimeRecord.ClockOut, "UpcomingMealBreakEnd(EarlyStart)");
      return;
    }

    // Current time record is a meal break - user is on a meal break
    if (currentTimeRecord && this.timeRecordService.checkIsTimeRecordMealBreak(currentTimeRecord)) {
      this.setUserClockStatus(ClockState.MEAL_BREAK, currentTimeRecord.ClockIn, timeZoneId, currentTimeRecord);
      this.scheduleTimeout(currentTimeRecord.ClockOut, "MealBreakEnd");
      return;
    }

    let currentIsOpen = false;
    if (!upcomingTimeRecord && currentTimeRecord) {
      try {
        currentIsOpen = await this.timeRecordService.checkIsTimeRecordOpen(currentTimeRecord);
      } catch (error) {
        this.logService.error("AppUserClockState.LoadOpenStatusFailed", error);
        if (revision === this.loadRevision && user.Id === this.appUserService.currentUserId) {
          this.suspendAndRetry("Current time record open status is unavailable.");
        }
        return;
      }
      if (revision !== this.loadRevision || user.Id !== this.appUserService.currentUserId) { return; }
    }

    // No upcoming time record and the current time record is working and open - user is actively working
    if (!upcomingTimeRecord && currentTimeRecord && currentIsOpen && this.timeRecordService.checkIsTimeRecordWorking(currentTimeRecord)) {
      this.setUserClockStatus(ClockState.WORKING, currentTimeRecord.ClockIn, currentTimeRecord.ClockInTimeZoneId, currentTimeRecord);
      this.cancelTimeout();
      return;
    }

    // No upcoming time record and current time record is closed - user clocked out a bit early
    if (!upcomingTimeRecord && currentTimeRecord && !currentIsOpen) {
      this.setUserClockStatus(ClockState.CLOCKED_OUT, currentTimeRecord.ClockOut, currentTimeRecord.ClockOutTimeZoneId, null);
      openTimeRecord ? this.scheduleTimeout(openTimeRecord.ClockIn, "OpenTimeRecordStart") : this.cancelTimeout();
      return;
    }

    // No current or upcoming time record - either user hasn't clocked in today or has already clocked out and is past the clock out time
    if (!currentTimeRecord && !upcomingTimeRecord) {

      let latestClosedTimeRecord: TimeRecord | null = null;
      try {
        latestClosedTimeRecord = await this.timeRecordService.getLastestTimeRecordByUserIdDate(user.Id, now);
      } catch (err) {
        this.logService.error("AppUserClockState.LoadLatestClosedFailed", err);
      }
      if (revision !== this.loadRevision || user.Id !== this.appUserService.currentUserId) { return; }

      this.setUserClockStatus(
        ClockState.CLOCKED_OUT,
        latestClosedTimeRecord ? latestClosedTimeRecord.ClockOut : null,
        latestClosedTimeRecord ? latestClosedTimeRecord.ClockOutTimeZoneId : null,
        null
      );
      openTimeRecord ? this.scheduleTimeout(openTimeRecord.ClockIn, "OpenTimeRecordStart") : this.cancelTimeout();
      return;
    }

    this.logService.warn("AppUserClockState.UnhandledCase", { currentTimeRecord, upcomingTimeRecord }, true);
    this.setNullClockStatus("Unhandled case in load state. If you're seeing this, something needs to be fixed in the above logic.");
  }

  private setUserClockStatus(state: ClockState, sinceTime: DateTimeType | null, timeZoneId: string | null, activeTimeRecord: TimeRecord | null) {
    this.retryDelayMs = 1000;
    const prev = this.clockState;
    const formattedSince = sinceTime ? DateTimeHelper.formatDateTime(sinceTime) : null;
    if (prev && prev.state === state && prev.sinceTime === formattedSince && prev.timeZoneId === timeZoneId && prev.activeTimeRecord?.RecordId === activeTimeRecord?.RecordId && prev.activeTimeRecord?.ProjectId === activeTimeRecord?.ProjectId && prev.activeTimeRecord?.EmployeeId === activeTimeRecord?.EmployeeId) {
      return; // No change
    }
    this.clockStateSubject.next({ state, sinceTime: formattedSince, timeZoneId, activeTimeRecord });
    this.logService.info("AppUserClockState.UserClockStateUpdated", { 
      prevState: prev?.state, 
      newState: state, 
      sinceTime: formattedSince, 
      timeZoneId, 
      activeTimeRecordLocalId: activeTimeRecord?.RecordId, 
      activeTimeRecordOnlineId: activeTimeRecord?.Id 
    });
  }

  private setNullClockStatus(reason: string) {
    const prev = this.clockStateSubject.value;
    if (prev !== null) {
      this.clockStateSubject.next(null);
      this.logService.info("AppUserClockState.UserClockStateNulled", { reason, prevState: prev.state });
    }
  }

  /** Unknown is not confirmed Working; replace the boundary timer with bounded recovery until a current read succeeds. */
  private suspendAndRetry(reason: string): void {
    this.setNullClockStatus(reason);
    this.timeoutScheduler.schedule({
      id: AppUserClockStateService.TIMEOUT_ID,
      at: new Date(Date.now() + this.retryDelayMs),
      context: { reason: 'ClockStateRecovery' }
    });
    this.retryDelayMs = Math.min(30000, this.retryDelayMs * 2);
  }

  private scheduleTimeout(boundary: DateTimeType | null, reason: string) {
    if (!boundary) { this.cancelTimeout(); return; }
    const dateObj = new Date(boundary as any);
    if (Number.isNaN(dateObj.getTime()) || dateObj.getTime() <= Date.now()) {
      this.cancelTimeout();
      return;
    }

    this.timeoutScheduler.schedule({
      id: AppUserClockStateService.TIMEOUT_ID,
      at: dateObj,
      fireIfPast: false,
      context: { reason }
    });
  }

  private cancelTimeout() {
    this.timeoutScheduler.cancel(AppUserClockStateService.TIMEOUT_ID);
  }
}